/**
 * CuttingRoom embed configuration.
 */

import { parseCuttingRoomUrl } from '../../ducks/cutting_room/base_url';

/** Hash path to the embedded player */
export const CUTTING_ROOM_EMBED_PLAYER_PATH = '/#/embed/player' as const;

/** Hash path to the embedded projects view */
export const CUTTING_ROOM_EMBED_PROJECTS_PATH = '/#/projects' as const;

/**
 * CuttingRoom UI hidden inside Iconik. The lobby is CuttingRoom's own project
 * list; Iconik owns project selection instead.
 */
export const CUTTING_ROOM_HIDDEN_UI = ['lobby', 'header', 'sidebar'];

/**
 * CRL (CuttingRoom Resource Locator) connector name for Iconik.
 */
export const CUTTING_ROOM_CONNECTOR = 'iconik' as const;

/**
 * Delimiter between the asset id and the pinned version id in a CRL.
 * `!` is used rather than `:` as it cannot appear in a URL authority.
 */
const CUTTING_ROOM_CRL_VERSION_DELIMITER = '!' as const;

/**
 * Whether to pin an asset version in the CRLs we send.
 *
 * Off because CuttingRoom's connector does not resolve the `!<version id>`
 * suffix yet: a pinned CRL resolves to nothing there, and it fails silently
 * because CuttingRoom stores the CRL rather than rejecting it. Without the
 * suffix CuttingRoom resolves the latest version itself.
 *
 * TODO(FE-7886): turn pinning on and delete this gate once CuttingRoom resolves
 * the suffix. https://linear.app/iconik/issue/FE-7886/cuttingroom-enable-version-id-pinning-in-crls-and-remove-the-gate
 */
export const CUTTING_ROOM_CRL_VERSION_PINNING_ENABLED = false;

/**
 * Build a CRL (CuttingRoom Resource Locator) for an Iconik asset.
 * Format: `crl://iconik/<system domain id>/<asset id>[!<version id>]`
 *
 * The version segment is optional: without it CuttingRoom resolves the latest
 * version itself and pins whatever it resolved.
 */
export function buildCrl({
  assetId,
  systemDomainId,
  versionId,
}: {
  assetId: string;
  systemDomainId: string;
  versionId?: string;
}): string {
  const version = versionId
    ? `${CUTTING_ROOM_CRL_VERSION_DELIMITER}${versionId}`
    : '';

  return `crl://${CUTTING_ROOM_CONNECTOR}/${systemDomainId}/${assetId}${version}`;
}

/**
 * Build the full iframe src URL for the CuttingRoom embedded player.
 */
export function buildCuttingRoomPlayerUrl({
  baseUrl,
  view,
  projectId,
  hide,
  autoSSO,
}: {
  baseUrl: string;
  view?: 'project' | 'player';
  projectId?: string;
  hide?: string[];
  autoSSO?: boolean;
}): string {
  let path = '';
  const params = new URLSearchParams();

  if (view === 'project') {
    path = `${CUTTING_ROOM_EMBED_PROJECTS_PATH}/${projectId ?? ''}`;
  } else {
    path = CUTTING_ROOM_EMBED_PLAYER_PATH;
    if (projectId) {
      params.set('projectId', projectId);
    }
  }

  if (hide && hide.length > 0) {
    params.set('hide', hide.join(','));
  }

  if (autoSSO) {
    params.set('autoSSO', '');
  }

  const query = params.toString();
  const separator = query ? '?' : '';

  return `${baseUrl}${path}${separator}${query}`;
}

function parseCuttingRoomRegionAndTenant(baseUrl: string): {
  region: string;
  tenant: string;
} {
  const parsed = parseCuttingRoomUrl(baseUrl);
  if (!parsed) {
    throw new Error(
      'CuttingRoom URL must include tenant and region subdomains of .cuttingroom.com',
    );
  }
  return { region: parsed.region, tenant: parsed.tenant };
}

/**
 * Build the `external_id` stored on an NLE_PROJECT asset to link it
 * back to the corresponding CuttingRoom project.
 *
 * Format: `cutting_room:<region>:<tenant>:project:<projectId>`
 */
export function buildCuttingRoomExternalId(
  projectId: string,
  baseUrl: string,
): string {
  const { region, tenant } = parseCuttingRoomRegionAndTenant(baseUrl);
  return `cutting_room:${region}:${tenant}:project:${projectId}`;
}

const CUTTING_ROOM_EXTERNAL_ID_PREFIX = 'cutting_room:';
const CUTTING_ROOM_PROJECT_SEGMENT = ':project:';

/**
 * Check whether an external_id belongs to a CuttingRoom project.
 */
export function isCuttingRoomExternalId(
  externalId: string | null | undefined,
): externalId is string {
  return (
    typeof externalId === 'string' &&
    externalId.startsWith(CUTTING_ROOM_EXTERNAL_ID_PREFIX) &&
    externalId.includes(CUTTING_ROOM_PROJECT_SEGMENT)
  );
}

/**
 * Extract the CuttingRoom project ID from an external_id.
 * Returns `undefined` when the external_id is not a CuttingRoom id.
 *
 * Format: `cutting_room:<region>:<tenant>:project:<projectId>`
 */
export function parseCuttingRoomProjectId(
  externalId: string | null | undefined,
): string | undefined {
  if (!isCuttingRoomExternalId(externalId)) {
    return undefined;
  }
  const index = externalId.indexOf(CUTTING_ROOM_PROJECT_SEGMENT);
  return externalId.slice(index + CUTTING_ROOM_PROJECT_SEGMENT.length);
}
